import { useEffect, useMemo, useState } from "react";
import { Button, Col, Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
  formatCalendarDateForInput,
  formatPhoneNumber,
  getCaseId,
  getClientId,
} from "../../../Utils/helper";
import { api_without_cancellation } from "../../../api/api";

import WitnessInformationFields from "./fields/WitnessInformationFields";
import WitnessContactFields from "./fields/WitnessContactFields";
import WitnessEmployerFields from "./fields/WitnessEmployerFields";
import WitnessStatementSummaryFields from "./fields/WitnessStatementSummaryFields";
// import InsuranceDetailsFields from "./fields/insurance/InsuranceDetailsFields";
// import InsuranceCompanyFields from "./fields/insurance/InsuranceCompanyFields";
// import InsuranceAdjusterFields from "./fields/insurance/InsuranceAdjusterFields";
// import InsuranceSupervisorFields from "./fields/insurance/InsuranceSupervisorFields";
import CounselFileInformationFields from "./fields/counsel/CounselFileInformationFields";
import CounselFirmFields from "./fields/counsel/CounselFirmFields";
import CounselAttorneyFields from "./fields/counsel/CounselAttorneyFields";
import CounselSupervisingAttorneyFields from "./fields/counsel/CounselSupervisingAttorneyFields";

// ── TabGrid ──────────────────────────────────────────────────────────────────
const TabGrid = ({ tabs, selectedId, onSelect, onDelete, onAdd }) => (
  <div className="w-100 m-t-5 p-l-5 p-r-5" style={{ minHeight: "25px" }}>
    <div
      className="overflow-scroll invisible-scrollbar"
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${Math.ceil(((tabs?.length ?? 0) + (onAdd ? 1 : 0)) / 4)}, 1fr)`,
        gridAutoFlow: "column",
        gap: "5px",
      }}
    >
      {tabs?.map((item) => {
        const isSelected = selectedId === item.id;
        return (
          <div key={item.id} style={{ position: "relative", display: "flex" }}>
            <button
              type="button"
              className={`event-type-button btn-hover-event-litigation-act height-25 ${isSelected ? "selected" : "unselected"}`}
              onClick={() => onSelect(item.id)}
              style={{ flex: 1, paddingRight: onDelete ? "5px" : undefined }}
            >
              <span className="text-nowrap">{item.label}</span>
            </button>
            {onDelete && (
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "0px",
                  color: isSelected ? "white" : "black",
                  fontSize: 14,
                  cursor: "pointer",
                }}
                title="Remove"
              >
                ×
              </span>
            )}
          </div>
        );
      })}
      {onAdd && (
        <button
          type="button"
          className="event-type-button height-25 unselected"
          onClick={onAdd}
          title="Add another"
        >
          <span className="text-nowrap">Add Another</span>
        </button>
      )}
    </div>
  </div>
);

// ── FormSection ───────────────────────────────────────────────────────────────
const FormSection = ({ firstTab, secondTab, _entityId, statesAbrs, isEdit, partiesData }) => {
  if (firstTab === "witness") {
    if (secondTab === "information")
      return <WitnessInformationFields isEdit={isEdit} partiesData={partiesData} />;
    if (secondTab === "contact") return <WitnessContactFields statesAbrs={statesAbrs} />;
    if (secondTab === "employer") return <WitnessEmployerFields statesAbrs={statesAbrs} />;
    if (secondTab === "statementSummary") return <WitnessStatementSummaryFields />;
  }

  // if (firstTab === "insurance") {
  //   if (secondTab === "insurance") return <InsuranceDetailsFields entityId={entityId} />;
  //   if (secondTab === "company")
  //     return <InsuranceCompanyFields entityId={entityId} stateAbrs={statesAbrs} />;
  //   if (secondTab === "adjuster")
  //     return <InsuranceAdjusterFields entityId={entityId} stateAbrs={statesAbrs} />;
  //   if (secondTab === "supervisor")
  //     return <InsuranceSupervisorFields entityId={entityId} stateAbrs={statesAbrs} />;
  // }

  if (firstTab === "counsel") {
    if (secondTab === "fileInformation") return <CounselFileInformationFields entityId={1} />;
    if (secondTab === "firm") return <CounselFirmFields entityId={1} statesData={statesAbrs} />;
    if (secondTab === "attorney")
      return <CounselAttorneyFields entityId={1} statesData={statesAbrs} />;
    if (secondTab === "supervisingAttorney")
      return <CounselSupervisingAttorneyFields entityId={1} statesData={statesAbrs} />;
  }

  return null;
};

// ── Static config ─────────────────────────────────────────────────────────────
const FIRST_TABS = [
  { id: "witness", label: "Witness" },
  // { id: "insurance", label: "Insurance" },
  { id: "counsel", label: "Counsel" },
];

const SECOND_TABS = {
  witness: [
    { id: "information", label: "Information" },
    { id: "contact", label: "Contact" },
    { id: "employer", label: "Employer" },
    { id: "statementSummary", label: "Statement Summary" },
  ],
  // insurance: [
  //   { id: "insurance", label: "Insurance" },
  //   { id: "company", label: "Company" },
  //   { id: "adjuster", label: "Adjuster" },
  //   { id: "supervisor", label: "Supervisor" },
  // ],
  counsel: [
    { id: "fileInformation", label: "File Information" },
    { id: "firm", label: "Firm" },
    { id: "attorney", label: "Attorney" },
    { id: "supervisingAttorney", label: "Supervising Attorney" },
  ],
};

const COLLECTION_TABS = ["insurance"];
const LABEL = { insurance: "Insurance" };

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const newEntity = () => ({ id: uid(), data: {} });

const WITNESS_DEFAULTS = {
  is_blocked: false,
  witness_birthday: null,
  gender: "",
  ssn: "",
  driver_license: "",
  RepLetterSent: null,
  contact_confirmed_date: null,
  witness_for_entity: "",
  witness_for_record_id: null,
  first_name: "",
  middle_name: "",
  last_name: "",
  home_address1: "",
  home_address2: "",
  home_city: "",
  home_state: "",
  home_zip: "",
  home_phone: "",
  home_extension: "",
  home_fax: "",
  home_email: "",
  home_website: "",
  witness_employer: "",
  work_name: "",
  work_address1: "",
  work_address2: "",
  work_city: "",
  work_state: "",
  work_zip: "",
  work_phone: "",
  work_extension: "",
  work_fax: "",
  work_email: "",
  work_website: "",
  statement_summary: "",
};

// ── AddWitnessModal ───────────────────────────────────────────────────────────
const AddWitnessModal = ({
  show,
  handleClose,
  refetchWitnesses,
  isDummy = false,
  isEdit,
  witness = {},
  deleteConfirmPopup,
  retainedByList,
  statesAbrs,
}) => {
  const initialEntities = useMemo(
    () => ({
      insurance: [newEntity()],
      counsel: [newEntity()],
    }),
    []
  );

  const methods = useForm({ defaultValues: { ...WITNESS_DEFAULTS } });
  const { register, setValue, handleSubmit, reset, unregister, watch } = methods;

  const [firstTab, setFirstTab] = useState("witness");
  const [secondTab, setSecondTab] = useState(SECOND_TABS.witness[0].id);
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState(initialEntities);
  const [activeEntityId, setActiveEntityId] = useState({
    insurance: initialEntities.insurance[0].id,
    counsel: initialEntities.counsel[0].id,
  });

  useEffect(() => {
    setSecondTab(SECOND_TABS[firstTab][0].id);
  }, [firstTab]);

  const isCollection = COLLECTION_TABS.includes(firstTab);
  const currentList = isCollection ? entities[firstTab] : null;
  const currentEntityId = isCollection ? activeEntityId[firstTab] : null;

  const addEntity = () => {
    const entity = newEntity();
    setEntities((prev) => ({ ...prev, [firstTab]: [...prev[firstTab], entity] }));
    setActiveEntityId((prev) => ({ ...prev, [firstTab]: entity.id }));
  };

  const deleteEntity = (idToDelete) => {
    unregister(`${firstTab}.${idToDelete}`);
    setEntities((prev) => {
      const list = prev[firstTab];
      if (list.length === 1) {
        const fresh = newEntity();
        setActiveEntityId((p) => ({ ...p, [firstTab]: fresh.id }));
        return { ...prev, [firstTab]: [fresh] };
      }
      const next = list.filter((e) => e.id !== idToDelete);
      if (idToDelete === activeEntityId[firstTab]) {
        const deletedIdx = list.findIndex((e) => e.id === idToDelete);
        const fallback = next[deletedIdx] ?? next[deletedIdx - 1] ?? next[0];
        setActiveEntityId((p) => ({ ...p, [firstTab]: fallback.id }));
      }
      return { ...prev, [firstTab]: next };
    });
  };

  const selectEntity = (id) => {
    setActiveEntityId((prev) => ({ ...prev, [firstTab]: id }));
  };

  const thirdTabs = useMemo(() => {
    if (!currentList) return [];
    return currentList.map((entity, idx) => ({
      id: entity.id,
      label: `${LABEL[firstTab]} ${idx + 1}`,
    }));
  }, [currentList, firstTab]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const fmt = (v) => formatPhoneNumber(v) || "";

  const isNonEmpty = (obj) =>
    obj &&
    Object.values(obj).some((v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim() !== "";
      if (typeof v === "object") return Object.values(v).some(Boolean);
      return true;
    });

  const buildEntityFromCounsel = (c) => ({
    opposing_counsel_id: c?.id ?? null,
    counsel_type_id: c.counsel_type?.id ?? null,
    file_number: c.file_number ?? "",
    orignal_lien: c.orignal_lien ?? null,
    final_lien: c.final_lien ?? null,

    counsel_id: c?.firm_directory_record ?? null,
    counsel_name: c.opposingcounselcontact?.name ?? "",
    counsel_address1: c.opposingcounselcontact?.address1 ?? "",
    counsel_address2: c.opposingcounselcontact?.address2 ?? "",
    counsel_city: c.opposingcounselcontact?.city ?? "",
    counsel_state: c.opposingcounselcontact?.state ?? "",
    counsel_zip: c.opposingcounselcontact?.zip ?? "",
    counsel_phone: fmt(c.opposingcounselcontact?.phone_number),
    counsel_extension: c.opposingcounselcontact?.phone_ext ?? "",
    counsel_fax: fmt(c.opposingcounselcontact?.fax),
    counsel_email: c.opposingcounselcontact?.email ?? "",
    counsel_website: c.opposingcounselcontact?.website ?? "",

    attorney_id: c?.attorney_directory_record?.id ?? null,
    attorney_dir_id: c.attorney_directory_record?.directory_id ?? null,
    attorney_first_name: c.opposingattorney?.first_name ?? "",
    attorney_last_name: c.opposingattorney?.last_name ?? "",
    attorney_address1: c.opposingattorney?.address1 ?? "",
    attorney_address2: c.opposingattorney?.address2 ?? "",
    attorney_city: c.opposingattorney?.city ?? "",
    attorney_state: c.opposingattorney?.state ?? "",
    attorney_zip: c.opposingattorney?.zip ?? "",
    attorney_phone: fmt(c.opposingattorney?.phone_number),
    attorney_extension: c.opposingattorney?.phone_ext ?? "",
    attorney_fax: fmt(c.opposingattorney?.fax),
    attorney_email: c.opposingattorney?.email ?? "",
    attorney_website: c.opposingattorney?.website ?? "",

    supervisor_attorney_id: c?.supervisor_directory_record?.id ?? null,
    supervisor_attorney_dir_id: c.supervisor_directory_record?.directory_id ?? null,
    supervisor_attorney_first_name: c.supervisor?.first_name ?? "",
    supervisor_attorney_last_name: c.supervisor?.last_name ?? "",
    supervisor_attorney_address1: c.supervisor?.address1 ?? "",
    supervisor_attorney_address2: c.supervisor?.address2 ?? "",
    supervisor_attorney_city: c.supervisor?.city ?? "",
    supervisor_attorney_state: c.supervisor?.state ?? "",
    supervisor_attorney_zip: c.supervisor?.zip ?? "",
    supervisor_attorney_phone: fmt(c.supervisor?.phone_number),
    supervisor_attorney_extension: c.supervisor?.phone_ext ?? "",
    supervisor_attorney_fax: fmt(c.supervisor?.fax),
    supervisor_attorney_email: c.supervisor?.email ?? "",
    supervisor_attorney_website: c.supervisor?.website ?? "",
  });

  useEffect(() => {
    if (!isEdit) {
      if (show && isDummy) setValue("is_blocked", true);
      return;
    }
    if (!witness || Object.keys(witness).length === 0) return;

    const counselEntities = (witness.witness_counsel ?? []).map((c) => ({
      id: 1,
      data: buildEntityFromCounsel(c),
    }));

    const ensureOne = (list, builder) => (list.length > 0 ? list : [{ id: 1, data: builder({}) }]);

    const finalCounsel = ensureOne(counselEntities, buildEntityFromCounsel);

    setEntities({ counsel: finalCounsel });
    setActiveEntityId({
      counsel: finalCounsel[0].id,
    });

    const counselMap = Object.fromEntries(finalCounsel.map((e) => [e.id, e.data]));

    reset({
      is_blocked: witness.is_blocked || false,
      ssn: witness.ssn ?? "",
      driver_license: witness.driver_license ?? "",
      witness_birthday: formatCalendarDateForInput(witness.witness_birthday) || null,
      gender: witness.witness_gender ?? witness.gender ?? "",
      RepLetterSent: formatCalendarDateForInput(witness.RepLetterSent) || null,
      contact_confirmed_date: formatCalendarDateForInput(witness.contact_confirmed_date) || null,
      witness_for_entity: witness.witness_for_entity ?? "",
      witness_for_record_id: witness.witness_for_record_id ?? null,

      first_name: witness.witness_first_name ?? witness.first_name ?? "",
      middle_name: witness.witness_middle_name ?? witness.middle_name ?? "",
      last_name: witness.witness_last_name ?? witness.last_name ?? "",

      home_address1: witness.witness_contact_home?.address1 ?? "",
      home_address2: witness.witness_contact_home?.address2 ?? "",
      home_city: witness.witness_contact_home?.city ?? "",
      home_state: witness.witness_contact_home?.state ?? "",
      home_zip: witness.witness_contact_home?.zip ?? "",
      home_phone: fmt(witness.witness_contact_home?.phone_number),
      home_extension: witness.witness_contact_home?.phone_ext ?? "",
      home_fax: fmt(witness.witness_contact_home?.fax),
      home_email: witness.witness_contact_home?.email ?? "",
      home_website: witness.witness_contact_home?.website ?? "",

      witness_employer: witness.witness_employer ?? "",
      work_name: witness.witness_work_contact?.name ?? "",
      work_address1: witness.witness_work_contact?.address1 ?? "",
      work_address2: witness.witness_work_contact?.address2 ?? "",
      work_city: witness.witness_work_contact?.city ?? "",
      work_state: witness.witness_work_contact?.state ?? "",
      work_zip: witness.witness_work_contact?.zip ?? "",
      work_phone: fmt(witness.witness_work_contact?.phone_number),
      work_extension: witness.witness_work_contact?.phone_ext ?? "",
      work_fax: fmt(witness.witness_work_contact?.fax),
      work_email: witness.witness_work_contact?.email ?? "",
      work_website: witness.witness_work_contact?.website ?? "",

      statement_summary: witness.statement_summary ?? "",
      statement_concerning: witness.statement_concerning ?? "",
      counsel: counselMap,
    });
  }, [isEdit, witness, show, reset]);

  // ── Payload builder ───────────────────────────────────────────────────────────
  const buildPayload = (values) => {
    const { is_blocked, counsel = {}, ...witnessFields } = values;
    return {
      is_blocked: !!is_blocked,
      ...witnessFields,
      counsels: Object.values(counsel).filter(isNonEmpty),
    };
  };

  const onSubmit = async (values) => {
    const counselMap = values.counsel ?? {};
    const counselEntries = Object.entries(counselMap);
    // Find first counsel entity that has any data but is missing counsel_type_id
    if (!values.is_blocked) {
      const invalidEntry = counselEntries.find(([, data]) => {
        if (!isNonEmpty(data)) return false; // empty rows are fine — buildPayload drops them
        return !data.counsel_type_id;
      });

      if (invalidEntry) {
        const [badId] = invalidEntry;
        setFirstTab("counsel");
        setSecondTab("fileInformation");
        setActiveEntityId((prev) => ({ ...prev, counsel: badId }));
        setTimeout(() => {
          methods.setError(`counsel.${badId}.counsel_type_id`, {
            type: "required",
            message: "Counsel type is required",
          });
        }, 0);
        return;
      }
      if (!values.witness_for_record_id) {
        setFirstTab("witness");
        setSecondTab("information");
        setTimeout(() => {
          methods.setError("witness_for_record_id", {
            type: "required",
            message: "Entity is required",
          });
        }, 0);
        return;
      }
    }
    const payload = buildPayload(values);
    try {
      setLoading(true);
      const url = `/api/witnesses/create_update/${getClientId()}/${getCaseId()}/`;
      const res = isEdit
        ? await api_without_cancellation.patch(url, { id: witness.id, ...payload })
        : await api_without_cancellation.post(url, payload);
      if (res.status === 200 || res.status === 201) {
        await refetchWitnesses();
        handleClose();
      }
    } catch (err) {
      console.error("Witness save failed:", err.response?.data ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  // const onInvalid = (errors) => {
  //   if (methods.getValues("is_blocked")) {
  //     methods.clearErrors();
  //     onSubmit(methods.getValues());
  //     return;
  //   }
  //   console.warn("Validation errors:", errors);
  // };

  const [partiesData, setPartiesData] = useState([]);

  const combineParties = (partiesData) => {
    const combinedList = [];

    // Add clients
    if (partiesData?.clients) {
      partiesData?.clients?.forEach((client) => {
        combinedList.push({
          id: client.id,
          type: "Client",
          name: `${client?.first_name || ""} ${client?.last_name || ""} - Client`.trim(),
        });
      });
    }

    // Add defendants
    if (partiesData?.defendants) {
      partiesData?.defendants?.forEach((defendant) => {
        combinedList.push({
          id: defendant.id,
          type: "Defendant",
          name: `${
            defendant?.defendantType?.id === 1
              ? `${defendant?.first_name || ""} ${defendant?.last_name || ""}`.trim()
              : defendant?.entity_name || ""
          } - Defendant`,
        });
      });
    }

    // Add other parties
    if (partiesData?.other_parties) {
      partiesData?.other_parties?.forEach((party) => {
        combinedList.push({
          id: party.id,
          type: "OtherParty",
          name: `${party?.party_first_name || party?.first_name || ""} ${party?.party_last_name || party?.last_name || ""} - OtherParty`.trim(),
        });
      });
    }

    if (partiesData?.witnesses) {
      partiesData?.witnesses?.forEach((witness) => {
        combinedList.push({
          id: witness.id,
          type: "Witness",
          name: `${witness?.witness_first_name || ""} ${witness?.witness_last_name || ""} - Witness`.trim(),
        });
      });
    }
    return combinedList;
  };

  const fetchPartiesData = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/litigation-page/defendants-and-other-parties/${getClientId()}/${getCaseId()}/`,
        {
          params: { litigation_event_popup: true },
        }
      );
      setPartiesData(combineParties(response.data));
    } catch (err) {
      console.error("error", err);
    }
  };

  useEffect(() => {
    fetchPartiesData();
  }, []);
  const dummy = watch("is_blocked");
  return (
    <FormProvider {...methods}>
      <Modal show={show} onHide={handleClose} dialogClassName="modal-dialog-centered modal-800p">
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            {isEdit ? "Edit Witness" : "Add Witness"}
          </div>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          <form id="addWitnessForm" onSubmit={handleSubmit(onSubmit)}>
            <div
              style={{ minHeight: "500px" }}
              className="custom-tab m-t-5 m-b-5 defendant-modal-content"
            >
              {!isEdit && (
                <Col md={12} className="d-flex align-items-center justify-content-center">
                  <label className="mb-0" style={{ textTransform: "uppercase", fontWeight: "600" }}>
                    No Witnesses
                  </label>
                  <input
                    type="checkbox"
                    id="is_blocked_witness"
                    {...register("is_blocked")}
                    style={{ marginRight: "5px", marginLeft: "5px" }}
                    className="cursor-pointer"
                  />
                  <label className="mb-0" style={{ textTransform: "uppercase", fontWeight: "600" }}>
                    Check this box if there are no witnesses on the case to mark page as Complete
                  </label>
                </Col>
              )}
              <fieldset
                disabled={dummy}
                style={{
                  border: 0,
                  padding: 0,
                  margin: 0,
                  opacity: dummy ? 0.5 : 1,
                  pointerEvents: dummy ? "none" : "auto",
                }}
              >
                <TabGrid tabs={FIRST_TABS} selectedId={firstTab} onSelect={setFirstTab} />

                <div className="bg-primary text-center height-25 d-flex align-items-center p-0 m-t-5">
                  <h5
                    className="text-white mx-auto"
                    style={{ fontSize: 14, textTransform: "capitalize" }}
                  >
                    {FIRST_TABS.find((t) => t.id === firstTab)?.label} View
                  </h5>
                </div>

                <TabGrid
                  tabs={SECOND_TABS[firstTab]}
                  selectedId={secondTab}
                  onSelect={setSecondTab}
                />

                {isCollection && (
                  <TabGrid
                    tabs={thirdTabs}
                    selectedId={currentEntityId}
                    onSelect={selectEntity}
                    onDelete={deleteEntity}
                    onAdd={addEntity}
                  />
                )}

                <FormSection
                  key={`${firstTab}-${secondTab}-${currentEntityId ?? "single"}`}
                  firstTab={firstTab}
                  secondTab={secondTab}
                  entityId={currentEntityId}
                  statesAbrs={statesAbrs}
                  retainedByList={retainedByList}
                  isEdit={isEdit}
                  partiesData={partiesData}
                />
              </fieldset>
              <div
                style={{ position: "absolute", bottom: "0px" }}
                className="d-flex w-100 justify-content-between custom-margin-top p-b-5 p-l-5 p-r-5"
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  className="height-25"
                  style={{ padding: "0px 12px" }}
                >
                  Cancel
                </Button>
                {isEdit && (
                  <Button
                    variant="danger"
                    type="button"
                    onClick={deleteConfirmPopup}
                    className="height-25"
                    style={{ padding: "0px 12px" }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="success"
                  type="submit"
                  className="m-l-5 bg-success height-25"
                  style={{ padding: "0px 12px" }}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save and Close"}
                </Button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </FormProvider>
  );
};

export default AddWitnessModal;
